import {defineStore} from 'pinia';
import {computed, reactive, Ref, ref} from "vue";
import type {Cluster, ComponentList, DeviceSettings, DisconnectPolicyDisplayState, LoadingDiagnostics} from "./types.ts";
import {WrapperSubscription, clearFunctionPropWarnings} from "./channel/WrapperSubscription.ts";
import {enableWorkers} from "./worker/WorkerInit.ts";
import UpdateCoordinator from "./utils/UpdateCoordinator";
import {ConnectionState} from "./socket/ConnectionState.ts";
import {CloneTransform} from "./layout/LayoutManager.ts";
import {FontUrl} from "./utils/FontLoader.ts";
import type {SwitchContext} from "./switching/types";
import {resetBatchCoordinator} from "./utils/SubscriptionBatchCoordinator.ts";
import type {ChannelFieldMap} from "./utils/ChannelFieldCollector.ts";
import {socketManager, channelManager, ruleManager, playlistManager} from "./services/managers.ts";
import {CacheManager} from "./cache/CacheManager.ts";

export const useStore = defineStore('component', () => {

    // --- Layout & components ---
    const layout: Ref<number | null, number | null> = ref(null);
    const layoutData = ref<any[]>([]);
    const homeData = ref<any[]>([]);
    const loaded = ref(false);
    // see docs/EXTRACTED_COMMENTS.md#c143
    const layoutPending = ref(false);
    const ratio = ref(0);
    const device_settings = ref<DeviceSettings>();
    const components = ref<ComponentList>({});
    const datasheets = ref([] as string[]);
    const adoptions = reactive(new Map<string, Array<WrapperSubscription>>);
    const anchors = reactive(new Map<string, Set<string>>());
    const cluster = reactive<Cluster>({enabled: false, row_start: 0, channel: 0});
    const submitions = reactive(new Map<string, string>());

    // see docs/EXTRACTED_COMMENTS.md#c144
    const clone = reactive({
        transform: {} as CloneTransform,
        transformHash: "",
        // see docs/EXTRACTED_COMMENTS.md#c145
        containerTransforms: [] as CloneTransform[],
    });

    // --- Font loading ---
    const fonts = reactive({
        urls: [] as FontUrl[],
        required: new Set<string>(),
        mainThreadLoaded: new Set<string>(),
        workerReady: new Set<string>(),
    });

    // --- Channel data ---
    const channelFieldMap: ChannelFieldMap = reactive(new Map<number, Set<number>>());

    // --- Connection ---
    const connection = reactive({
        state: ConnectionState.Disconnected as ConnectionState,
        connectedAt: null as number | null,
        lastDisconnectedAt: null as number | null,
    });
    const connected = computed(() => connection.state !== ConnectionState.Disconnected);

    // --- Error / disconnect policy ---
    const error = ref("");
    const errorBarVisible = ref<boolean>(false);
    const layoutUnresolved = ref<boolean>(false);
    const disconnectPolicyDisplay = ref<DisconnectPolicyDisplayState>({
        visible: false, text: null, image_url: null, source: null, activated_at: null,
    });

    // --- UI state ---
    const ui = reactive({
        settingsOpen: false,
        statusBarVisible: false,
        statusBarAutoShown: false,
    });
    const currentDate = ref(Date.now());

    // see docs/EXTRACTED_COMMENTS.md#c146
    const loadingDiagnostics = ref<LoadingDiagnostics | null>(null);

    // --- Transition switching contexts ---
    const switchContexts = reactive<Record<string, SwitchContext>>({});

    // -------------------------------------------------------------------------

    setInterval(() => { currentDate.value = Date.now(); }, 100);

    const clearComponents = () => {
        components.value = {};
        datasheets.value = [];
        // see docs/EXTRACTED_COMMENTS.md#c147
        channelFieldMap.clear();
        loadingDiagnostics.value = null;
        clearFunctionPropWarnings();
    };

    /* see docs/EXTRACTED_COMMENTS.md#c148 */
    const removeComponent = (id: string): void => {
        if (!components.value[id]) return;

        channelManager.removeSubscriptionFromAllChannels(id);
        ruleManager.removeCallbacksForOwner(id);

        // see docs/EXTRACTED_COMMENTS.md#c149
        for (const [key, subs] of adoptions.entries()) {
            const next = subs.filter((s) => s.componentId !== id);
            if (next.length === 0) {
                adoptions.delete(key);
            } else if (next.length !== subs.length) {
                adoptions.set(key, next);
            }
        }

        delete components.value[id];
    };

    async function reset() {
        CacheManager.instance.beginTransition("store.reset");
        try {
            // see docs/EXTRACTED_COMMENTS.md#c150
            channelManager.clearAllSubscriptions();

            clearComponents();
            adoptions.clear();
            // see docs/EXTRACTED_COMMENTS.md#c151
            UpdateCoordinator.clear();
            resetBatchCoordinator();
            await channelManager.clearChannels();
            socketManager.clear();
            ruleManager.clearRules();
            playlistManager.clearPlaylists();
            anchors.clear();
            submitions.clear();
            error.value = "";
            for (const key of Object.keys(switchContexts)) {
                delete switchContexts[key];
            }
        } finally {
            CacheManager.instance.endTransition("store.reset");
        }
    }

    async function loadData() { enableWorkers(); }

    const showDisconnectPolicyDisplay = (payload: Omit<DisconnectPolicyDisplayState, "visible">) => {
        disconnectPolicyDisplay.value = {...payload, visible: true};
    };

    const clearDisconnectPolicyDisplay = () => {
        disconnectPolicyDisplay.value = {
            visible: false, text: null, image_url: null, source: null, activated_at: null,
        };
    };

    const ensureSwitchContext = (containerId: string): SwitchContext => {
        const existing = switchContexts[containerId];
        if (existing) return existing;
        const created: SwitchContext = {
            containerId, activeId: null, previousId: null,
            currentState: "active", previousState: "exit",
            variant: "crossfade", inMs: 0, outMs: 0, ease: "linear",
            token: 0, phase: "idle", finalizeToken: null,
        };
        switchContexts[containerId] = created;
        return created;
    };

    const setSwitchContext = (containerId: string, patch: Partial<Omit<SwitchContext, "containerId">>) => {
        Object.assign(ensureSwitchContext(containerId), patch);
    };

    const requestSwitchFinalize = (containerId: string, token: number) => {
        ensureSwitchContext(containerId).finalizeToken = token;
    };

    const clearSwitchFinalize = (containerId: string, token?: number) => {
        const ctx = switchContexts[containerId];
        if (!ctx) return;
        if (token !== undefined && ctx.finalizeToken !== token) return;
        ctx.finalizeToken = null;
    };

    return {
        // Layout & components
        layout, layoutData, homeData, loaded, layoutPending,
        ratio, device_settings, components, datasheets,
        adoptions, anchors, cluster, submitions,

        // Clone / transform
        clone, clearComponents, removeComponent,

        // Font loading
        fonts,

        // Channel data
        channelFieldMap,

        // Connection
        connection, connected,

        // see docs/EXTRACTED_COMMENTS.md#c152
        socketManager, channelManager, ruleManager, playlistManager,

        // Error / disconnect policy
        error, errorBarVisible, layoutUnresolved,
        disconnectPolicyDisplay, showDisconnectPolicyDisplay, clearDisconnectPolicyDisplay,

        // UI state
        ui, currentDate, loadingDiagnostics,

        // Transition switching contexts
        switchContexts, ensureSwitchContext, setSwitchContext, requestSwitchFinalize, clearSwitchFinalize,

        // Actions
        reset, loadData,
    };
});

export default useStore;
